import type { Node } from '../../../models/scene';

export interface ResolveNodeResultFound {
  status: 'found';
  node: Node;
}

export interface ResolveNodeResultAmbiguous {
  status: 'ambiguous';
  candidates: Node[];
}

export interface ResolveNodeResultNotFound {
  status: 'not_found';
}

export type ResolveNodeResult = ResolveNodeResultFound | ResolveNodeResultAmbiguous | ResolveNodeResultNotFound;

const normalize = (value: string) => value.trim().toLowerCase();

const levenshteinDistance = (a: string, b: string): number => {
  const matrix = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));

  for (let i = 0; i <= a.length; i += 1) matrix[i][0] = i;
  for (let j = 0; j <= b.length; j += 1) matrix[0][j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }

  return matrix[a.length][b.length];
};

export function resolveNodeByName(name: string, nodes: Node[], maxDistance = 2): ResolveNodeResult {
  const query = normalize(name);
  if (!query) return { status: 'not_found' };

  const exactMatches = nodes.filter((node) => normalize(node.name ?? '') === query);
  if (exactMatches.length === 1) return { status: 'found', node: exactMatches[0] };
  if (exactMatches.length > 1) return { status: 'ambiguous', candidates: exactMatches };

  const ciMatches = nodes.filter((node) => normalize(node.name ?? '').includes(query));
  if (ciMatches.length === 1) return { status: 'found', node: ciMatches[0] };
  if (ciMatches.length > 1) return { status: 'ambiguous', candidates: ciMatches };

  const fuzzyCandidates = nodes
    .map((node) => ({ node, distance: levenshteinDistance(normalize(node.name ?? ''), query) }))
    .filter((entry) => entry.node.name && entry.distance <= maxDistance)
    .sort((a, b) => a.distance - b.distance);

  if (fuzzyCandidates.length === 0) return { status: 'not_found' };
  if (fuzzyCandidates.length === 1) return { status: 'found', node: fuzzyCandidates[0].node };

  const minDistance = fuzzyCandidates[0].distance;
  const close = fuzzyCandidates.filter((entry) => entry.distance === minDistance).map((entry) => entry.node);
  if (close.length === 1) return { status: 'found', node: close[0] };

  return { status: 'ambiguous', candidates: close };
}
